// cookie-manager.ts
import axios from 'axios';
import store from '@/store';
import {bindConsent, getPixelIdForPath} from '@/helpers/meta/metaTrack';
import {MetaEventParamsMap, MetaTrackEvents} from '@/helpers/meta/metaEvents';

type CookieSettings = { version: number; functional: true; analytics: boolean };
type ConsentListener = (allowed: boolean) => void;

/* -------------------- Tracker (CAPI proxy) Typen -------------------- */
type TrackerUserData = {
    fbp?: string;
    fbc?: string;
    // IP & UA setzt/hasht der Server aus Request-Headern
    external_id?: string;              // optional (falls vorhanden)
};
type TrackerEvent = {
    event_name: string;
    event_time: number;                // UNIX seconds
    event_id: string;                  // vom Client generiert
    action_source: 'website';
    pixel_id: string;
    event_source_url?: string;
    user_data: TrackerUserData;
    custom_data: Record<string, unknown>;
};

export default class CookieManager {
    private _version = 2;
    private _cookies: CookieSettings = {version: this._version, functional: true, analytics: false};
    private _listeners: ConsentListener[] = [];

    constructor() {
        try {
            const raw = localStorage.getItem('cookieSettings');
            if (raw) {
                try {
                    const parsed = JSON.parse(raw) as Partial<CookieSettings>;
                    this._cookies = {version: this._version, functional: true, analytics: !!parsed?.analytics};
                } catch {
                    this._cookies = {version: this._version, functional: true, analytics: false};
                }
            } else {
                setTimeout(() => this.showCookieDialog(), 500);
            }
        } catch {
            this._cookies = {version: this._version, functional: true, analytics: false};
            setTimeout(() => this.showCookieDialog(), 500);
        }

        // Meta-Pixel Consent Binding
        bindConsent(
            () => this._cookies.analytics,
            (cb) => this.onConsentChange(cb),
        );
    }

    /* -------------------- Consent UI -------------------- */

    public onConsentChange(fn: ConsentListener): void {
        this._listeners.push(fn);
    }

    private _emit(): void {
        const allowed = !!this._cookies.analytics;
        for (const fn of this._listeners) {
            try {
                fn(allowed);
            } catch { /* noop */
            }
        }
    }

    public showCookieDialog(): void {
        store.commit('showCookieDialog', true);
    }

    public hideCookieDialog(): void {
        store.commit('showCookieDialog', false);
    }

    public getCookieSettings(): CookieSettings {
        return this._cookies;
    }

    public async resetTrackingCookie(): Promise<void> {
        try {
            this._cookies.analytics = false;
            this._save();
            await axios.delete(`${process.env.VUE_APP_API_PATH}/tracker/cookie`, {withCredentials: true});
            this._emit();
        } finally {
            this.hideCookieDialog();
        }
    }

    public async setTrackingCookie(): Promise<void> {
        try {
            this._cookies.analytics = true;
            this._save();
            await axios.post(`${process.env.VUE_APP_API_PATH}/tracker/cookie`, undefined, {withCredentials: true});
            this._emit();
        } finally {
            this.hideCookieDialog();
        }
    }

    /* -------------------- Gates -------------------- */

    /** Nur auf /anmelden* oder /bewerben* und mit Consent + geladenem Pixel tracken */
    public canTrack(pathname: string): boolean {
        const onSignup = pathname === '/anmelden' || pathname.startsWith('/anmelden/');
        const onRecruiting = pathname === '/bewerben' || pathname.startsWith('/bewerben/');
        const hasConsent = !!this._cookies.analytics;
        const fbqReady = typeof window !== 'undefined' && !!window.fbq;

        // Tracken nur erlaubt, wenn einer der beiden Pfade aktiv ist
        return (onSignup || onRecruiting) && hasConsent && fbqReady;
    }

    /** Für Server-Mirroring: Consent + /anmelden* oder /bewerben*, Pixel optional */
    private canShareToServer(pathname?: string): boolean {
        const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
        const onSignup = path === '/anmelden' || path.startsWith('/anmelden/');
        const onRecruiting = path === '/bewerben' || path.startsWith('/bewerben/');
        const hasConsent = !!this._cookies.analytics;

        return (onSignup || onRecruiting) && hasConsent;
    }

    /* -------------------- Session helpers -------------------- */

    private _safeGetSessionItem(k: string): string | null {
        try {
            return sessionStorage.getItem(k);
        } catch {
            return null;
        }
    }

    private _safeSetSessionItem(k: string, v: string): void {
        try {
            sessionStorage.setItem(k, v);
        } catch { /* noop */
        }
    }

    private _safeRemoveSessionItem(k: string): void {
        try {
            sessionStorage.removeItem(k);
        } catch { /* noop */
        }
    }

    public getSignupSessionId(pathname?: string): string {
        const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
        const pixelId = getPixelIdForPath(path) || 'default';
        const key = `signup.sid.${pixelId}`;

        let sid = this._safeGetSessionItem(key);
        if (!sid) {
            sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
            this._safeSetSessionItem(key, sid);
        }
        return sid;
    }

    private _getSentSet(pathname?: string): Set<string> {
        const sid = this.getSignupSessionId(pathname);
        const k = `signup.sent.${sid}`;
        const raw = this._safeGetSessionItem(k);
        let arr: string[] = [];
        if (raw) {
            try {
                arr = JSON.parse(raw) as string[];
            } catch {
                arr = [];
            }
        }
        return new Set(arr);
    }

    private _saveSentSet(set: Set<string>, pathname?: string): void {
        const sid = this.getSignupSessionId(pathname);
        const k = `signup.sent.${sid}`;
        this._safeSetSessionItem(k, JSON.stringify(Array.from(set)));
    }

    public resetSignupSession(): void {
        const sid = this._safeGetSessionItem('signup.sid');
        if (sid) this._safeRemoveSessionItem(`signup.sent.${sid}`);
        this._safeRemoveSessionItem('signup.sid');
    }

    private _save(): void {
        try {
            localStorage.setItem('cookieSettings', JSON.stringify(this._cookies));
        } catch { /* noop */
        }
    }

    /* -------------------- Meta context / event IDs -------------------- */

    private _getCookie(name: string): string | null {
        if (typeof document === 'undefined') return null;
        try {
            const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
            return m ? decodeURIComponent(m[2]) : null;
        } catch {
            return null;
        }
    }

    public getMetaCookies(): { fbp?: string; fbc?: string } {
        const fbp = this._getCookie('_fbp') || undefined;
        const fbc = this._getCookie('_fbc') || undefined;
        return {fbp, fbc};
    }

    public createEventId(label: string): string {
        const sid = this.getSignupSessionId();
        const counterKey = `signup.eid.${label}.${sid}`;
        const now = Date.now();
        let n = 0;
        try {
            n = parseInt(this._safeGetSessionItem(counterKey) || '0', 10) || 0;
        } catch { /* noop */
        }
        n += 1;
        this._safeSetSessionItem(counterKey, String(n));
        return `${label}.${sid}.${n}.${now}`;
    }

    public buildMetaContext(label: string) {
        const event_id = this.createEventId(label);
        const {fbp, fbc} = this.getMetaCookies();
        const sid = this.getSignupSessionId(window?.location?.pathname);

        const url = (typeof window !== 'undefined' ? window.location.href : '') || '';
        return {event_id, fbp, fbc, sid, url};
    }

    /* -------------------- Tracker (CAPI proxy) -------------------- */

    private async postMetaEventToTracker(body: TrackerEvent | TrackerEvent[]): Promise<void> {
        try {
            const base = process.env.VUE_APP_API_PATH || '';
            await axios.post(`${base}/tracker/meta-capi/events`, body, {
                withCredentials: true,
                timeout: 4000,
            });
        } catch {
            // silent – UX darf nicht leiden
        }
    }

    private buildTrackerEvent(
        evtName: string,
        customOrStdParams: Record<string, unknown> | undefined,
        pathname: string,
        label: string,
    ): undefined | { paramsWithId: Record<string, unknown>; trackerEvent: TrackerEvent } {
        const {event_id, fbp, fbc, url} = this.buildMetaContext(label);
        const paramsWithId = {...(customOrStdParams || {}), event_id};

        // 🧩 1) Pixel-ID anhand des Pfads bestimmen
        const pixelId = getPixelIdForPath(pathname);
        if (!pixelId) return;
        return {
            paramsWithId,
            trackerEvent: {
                event_name: evtName,
                event_time: Math.floor(Date.now() / 1000),
                event_id,
                pixel_id: pixelId,
                action_source: 'website',
                event_source_url: url,
                user_data: {fbp, fbc}, // IP/UA ergänzt der Server
                custom_data: customOrStdParams || {},
            },
        };
    }

    /** fbq senden (falls möglich) + immer (bei Consent) an Server spiegeln */
    private _dualSend(
        kind: 'track' | 'trackCustom',
        event: string,
        params: Record<string, unknown> | undefined,
        pathname: string,
        label: string, // Quelle für event_id
    ): void {
        if (!this.canShareToServer(pathname)) return;


        // 🧩 2) TrackerEvent zusammenbauen
        const eventResp = this.buildTrackerEvent(event, params, pathname, label);

        if (!eventResp) return;
        const {paramsWithId, trackerEvent} = eventResp;

        // 🧩 3) Browser Pixel-Event senden (nur wenn erlaubt)
        if (this.canTrack(pathname)) {
            try {
                window.fbq?.(kind as 'track', event, paramsWithId, {
                    eventID: paramsWithId.event_id as string | undefined,
                });
            } catch {
                /* noop */
            }
        }

        // 🧩 4) Mirror an Server (CAPI)
        this.postMetaEventToTracker(trackerEvent);
    }

    /* -------------------- Öffentliche Tracking-APIs -------------------- */

    // Standard-Events: typsicher an MetaEventParamsMap
    public fbqSafeTrack<K extends MetaTrackEvents>(
        event: K,
        params?: MetaEventParamsMap[K],
        pathname?: string,
        label?: string,
    ): void {
        const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
        const labelString = label || event.toString()
        this._dualSend('track', String(event), params as unknown as Record<string, unknown>, path, labelString);
    }

    public fbqTrackOnce<K extends MetaTrackEvents>(
        key: string,
        event: K,
        params?: MetaEventParamsMap[K],
        pathname?: string,
    ): void {
        const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');

        if (!this.canShareToServer(path)) return;


        const safeKey = key.length > 200 ? key.slice(0, 200) : key;
        const sent = this._getSentSet();

        if (sent.has(safeKey)) return;

        this._dualSend('track', String(event), params as unknown as Record<string, unknown>, path, safeKey);

        sent.add(safeKey);
        this._saveSentSet(sent);
    }

    public fbqSafeTrackCustom(
        event: string,
        params?: Record<string, unknown>,
        pathname?: string,
        label?: string,
    ): void {
        const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
        const safeLabel = label || event;
        this._dualSend('trackCustom', event, params, path, safeLabel);
    }

    public fbqTrackCustomOnce(
        key: string,
        event: string,
        params?: Record<string, unknown>,
        pathname?: string,
    ): void {
        const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
        if (!this.canShareToServer(path)) return;

        const safeKey = key.length > 200 ? key.slice(0, 200) : key;
        const sent = this._getSentSet();
        if (sent.has(safeKey)) return;

        this._dualSend('trackCustom', event, params, path, safeKey);

        sent.add(safeKey);
        this._saveSentSet(sent);
    }

    public fbqTrackIfChanged<K extends MetaTrackEvents>(
        sessionKey: string,
        currentValue: string,
        event: K,
        buildParams: (prev: string | null) => MetaEventParamsMap[K],
        pathname?: string,
    ): void {
        const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
        if (!this.canShareToServer(path)) return;

        const prev = this._safeGetSessionItem(sessionKey);
        if (prev === currentValue) return;

        try {
            const params = buildParams(prev);
            const label = `${String(event)}:${sessionKey}`;
            this._dualSend('track', String(event), params as unknown as Record<string, unknown>, path, label);
            this._safeSetSessionItem(sessionKey, currentValue);
        } catch {
            // noop
        }
    }

    public fbqTrackCustomIfChanged(
        key: string,
        value: string,
        event: string,
        paramsBuilder: (prev?: string) => Record<string, unknown>,
        pathname?: string,
    ): void {
        const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
        if (!this.canShareToServer(path)) return;

        const sid = this.getSignupSessionId();
        const k = `signup.custom.${key}.${sid}`;
        const prev = this._safeGetSessionItem(k);
        if (prev === value) return;

        try {
            const params = paramsBuilder(prev ?? undefined);
            const label = `${event}:${key}`;
            this._dualSend('trackCustom', event, params, path, label);
            this._safeSetSessionItem(k, value);
        } catch {
            // noop
        }
    }
}
